import React from "react"
import { useTranslation } from "react-i18next"
import {
  ProductAssociationApplyPopup,
  type ProductAssociationLabels,
  type ProductAssociationRequest,
} from "../../../components/Associations/ProductAssociationApplyPopup"
import { ESelectionApplyTargetType } from "../../../Enum/ESelectionApplyTargetType"
import type { ApplySelectionRequest } from "../../../hooks/Selection/useApplySelection"

interface SelectionApplyPopupProps {
  title: string
  actionLabel: string
  helpTitle: string
  helpDescription: string
  helpBullets: string[]
  helpKey: string
  onClose: () => void
  onConfirm: (request: ApplySelectionRequest) => Promise<void>
}

export const SelectionApplyPopup: React.FC<SelectionApplyPopupProps> = ({ onConfirm, ...props }) => {
  const { t } = useTranslation("selection")

  const labels: ProductAssociationLabels = {
    targetLegend: t("apply_target_legend"),
    allProducts: t("apply_target_all_products"),
    selectedProducts: t("apply_target_selected_products"),
    allMeals: t("apply_target_all_meals"),
    selectedMeals: t("apply_target_selected_meals"),
    allDrinks: t("apply_target_all_drinks"),
    selectedDrinks: t("apply_target_selected_drinks"),
    allCombos: t("apply_target_all_combos"),
    selectedCombos: t("apply_target_selected_combos"),
    selectMealsAction: t("apply_select_meals_action"),
    selectDrinksAction: t("apply_select_drinks_action"),
    selectCombosAction: t("apply_select_combos_action"),
    selectedMealsCount: t("apply_selected_meals"),
    selectedDrinksCount: t("apply_selected_drinks"),
    selectedCombosCount: t("apply_selected_combos"),
    selectedMealsTitle: t("apply_target_selected_meals"),
    selectedDrinksTitle: t("apply_target_selected_drinks"),
    selectedCombosTitle: t("apply_target_selected_combos"),
    noSelectedMeals: t("apply_no_selected_meals"),
    noSelectedDrinks: t("apply_no_selected_drinks"),
    noSelectedCombos: t("apply_no_selected_combos"),
  }

  const handleConfirm = (request: ProductAssociationRequest) =>
    onConfirm({ ...request, targetType: request.targetType as ESelectionApplyTargetType })

  return (
    <ProductAssociationApplyPopup
      {...props}
      labels={labels}
      targetValues={{
        allProducts: ESelectionApplyTargetType.AllProducts,
        selectedProducts: ESelectionApplyTargetType.SelectedProducts,
        allMeals: ESelectionApplyTargetType.AllMeals,
        selectedMeals: ESelectionApplyTargetType.SelectedMeals,
        allDrinks: ESelectionApplyTargetType.AllDrinks,
        selectedDrinks: ESelectionApplyTargetType.SelectedDrinks,
        allCombos: ESelectionApplyTargetType.AllCombos,
        selectedCombos: ESelectionApplyTargetType.SelectedCombos,
      }}
      interactionKeys={{
        closeDescriptionKey: "interactionDescriptions:pages.products.selection.selectionApplyPopup.button.onClose",
        targetDescriptionKey:
          "interactionDescriptions:pages.products.selection.selectionApplyPopup.select.applyTargetLegend",
        confirmDescriptionKey: "interactionDescriptions:pages.products.selection.selectionApplyPopup.button.actionLabel",
        addMealDescriptionKey: "interactionDescriptions:pages.products.selection.selectionApplyPopup.button.addMeal",
        removeMealDescriptionKey: "interactionDescriptions:pages.products.selection.selectionApplyPopup.button.removeMeal",
        addDrinkDescriptionKey: "interactionDescriptions:pages.products.selection.selectionApplyPopup.button.addDrink",
        removeDrinkDescriptionKey:
          "interactionDescriptions:pages.products.selection.selectionApplyPopup.button.removeDrink",
        addComboDescriptionKey: "interactionDescriptions:pages.products.selection.selectionApplyPopup.button.addCombo",
        removeComboDescriptionKey:
          "interactionDescriptions:pages.products.selection.selectionApplyPopup.button.removeCombo",
      }}
      onConfirm={handleConfirm}
    />
  )
}
